import { Combobox } from '../kit.js';
import { useEffect, useMemo, useState } from 'react';

import { buyerSearchFromText } from '../buyer-search.js';
import { useBuyers, useMerchants } from '../queries.js';

export type AuditEntitySelection = {
  readonly resourceId: string;
  readonly resourceType: string;
};

/** The field text of a selection restored from the URL, whose name this page has not loaded. */
const RESTORED_LABEL: Readonly<Record<string, string>> = {
  BUYER: 'خریدار انتخاب‌شده',
  MERCHANT: 'فروشگاه انتخاب‌شده',
};

/**
 * The «فروشگاه یا خریدار» filter of the audit toolbar: an ordinary combobox field whose server
 * suggestions open in the floating list (owner: the result list sat inline and pushed the toolbar
 * apart). The pick is the field value; emptying the field clears the entity filter.
 */
export function AuditEntitySearch({
  onResolved,
  resourceId,
  resourceType,
}: {
  readonly onResolved: (selection: AuditEntitySelection | null) => void;
  readonly resourceId: string;
  readonly resourceType: string;
}) {
  const selectedId =
    resourceId === '' || resourceType === '' ? null : `${resourceType}:${resourceId}`;
  // The name of the row picked here; a selection that came with the URL keeps a generic label.
  const [picked, setPicked] = useState<{ readonly id: string; readonly label: string } | null>(
    null,
  );
  const selectedLabel =
    selectedId === null
      ? null
      : picked?.id === selectedId
        ? picked.label
        : (RESTORED_LABEL[resourceType] ?? 'مورد انتخاب‌شده');
  const [input, setInput] = useState(selectedLabel ?? '');
  // A selection changed elsewhere (cleared filters, back navigation) resets the field text.
  useEffect(() => {
    setInput(selectedLabel ?? '');
  }, [selectedLabel]);
  // The search is the typed text, not the label of the current pick.
  const query = input === selectedLabel ? '' : input;

  const buyerSearch = useMemo(() => buyerSearchFromText(query), [query]);
  const buyerFilters = 'filters' in buyerSearch ? buyerSearch.filters : {};
  const buyerMessage = 'message' in buyerSearch ? buyerSearch.message : undefined;
  const merchants = useMerchants(
    query.trim().length >= 3 ? { q: query.trim() } : {},
    query.trim().length >= 3,
  );
  const buyers = useBuyers(buyerFilters, query.trim() !== '' && buyerMessage === undefined);
  const merchantRows = merchants.data?.pages.flatMap((page) => page.items) ?? [];
  const buyerRows = buyers.data?.pages.flatMap((page) => page.items) ?? [];
  const loading = merchants.isFetching || buyers.isFetching;
  const options = useMemo(() => {
    if (query.trim() === '') return [];
    const items: { label: string; value: string }[] = [];
    for (const row of merchantRows) {
      items.push({ label: `فروشگاه: ${row.displayNameFa}`, value: `MERCHANT:${row.id}` });
    }
    for (const row of buyerRows) {
      items.push({
        label: `خریدار: ${row.firstNameFa} ${row.lastNameFa}، ${row.mobileMasked}`,
        value: `BUYER:${row.buyerId}`,
      });
    }
    return items;
  }, [buyerRows, merchantRows, query]);

  function pick(id: string): void {
    const [type, ...rest] = id.split(':');
    const entityId = rest.join(':');
    if (type === undefined || entityId === '') {
      onResolved(null);
      return;
    }
    const label = options.find((option) => option.value === id)?.label;
    if (label !== undefined) setPicked({ id, label });
    onResolved({ resourceId: entityId, resourceType: type });
  }

  return (
    // In the filter-pill row: the label stays for screen readers only, and the field is pill height.
    <div className="admin-pill-search">
      <Combobox
        filterOptions={false}
        {...(query.trim() !== '' && buyerMessage !== undefined && merchantRows.length === 0
          ? { hint: buyerMessage }
          : {})}
        inputValue={input}
        label="فروشگاه یا خریدار"
        loading={loading}
        onChange={pick}
        onInputValueChange={(next) => {
          setInput(next);
          if (next.trim() === '' && selectedId !== null) {
            setPicked(null);
            onResolved(null);
          }
        }}
        options={options}
        placeholder="فروشگاه یا خریدار"
        selectedLabel={selectedLabel}
        value={selectedId ?? undefined}
      />
    </div>
  );
}
